import { Workorder } from '@certhon/models';

/**
 * Route pair for calculating routes between two locations
 */
export interface RoutePair {
  from: string;
  to: string;
}

/**
 * Format address for geocoding - normalize whitespace only.
 * Keep postal codes intact as they help Dutch geocoding accuracy.
 */
export function formatAddressForGeocoding(address: string): string {
  return address.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Build route pairs from consecutive workorders for route calculation.
 *
 * This function creates a sequence of route pairs that represent the path
 * a technician should take through their workorders:
 * - First workorder: route from HQ to workorder
 * - Consecutive workorders: route from previous workorder to next
 * - Via HQ option: splits route into two segments (prev -> HQ -> current)
 * - Final route: from last workorder back to HQ
 *
 * @param workorders - Ordered list of workorders to visit
 * @param hqAddress - Headquarters address (starting and ending point)
 * @returns Array of route pairs for API calculation
 *
 * @example
 * ```ts
 * const pairs = buildRoutePairs(workorders, 'ABC Westland 555 Poeldijk');
 * // Returns: [
 * //   { from: 'HQ address', to: 'First WO address' },
 * //   { from: 'First WO address', to: 'Second WO address' },
 * //   { from: 'Second WO address', to: 'HQ address' }
 * // ]
 * ```
 */
export function buildRoutePairs(
  workorders: Workorder[],
  hqAddress: string,
): RoutePair[] {
  const routePairs: RoutePair[] = [];

  for (let i = 0; i < workorders.length; i++) {
    const wo = workorders[i];
    const rawAddress = wo.location_address;

    if (!rawAddress) continue;

    const address = formatAddressForGeocoding(rawAddress);

    if (i === 0) {
      // First workorder: route from HQ
      routePairs.push({ from: hqAddress, to: address });
    } else {
      // Route from previous workorder
      const prevWo = workorders[i - 1];
      const prevRawAddress = prevWo.location_address;

      if (prevRawAddress) {
        const prevAddress = formatAddressForGeocoding(prevRawAddress);
        // Check if "via HQ" is selected
        if (wo.route_option === 'via_hq') {
          routePairs.push({ from: prevAddress, to: hqAddress });
          routePairs.push({ from: hqAddress, to: address });
        } else {
          routePairs.push({ from: prevAddress, to: address });
        }
      }
    }
  }

  // Add trailing route from last workorder back to HQ
  const lastWo = workorders[workorders.length - 1];
  const lastRawAddress = lastWo?.location_address;
  if (lastRawAddress) {
    const lastAddress = formatAddressForGeocoding(lastRawAddress);
    routePairs.push({ from: lastAddress, to: hqAddress });
  }

  return routePairs;
}
